import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MapPin, Phone, Mail, Pencil, Table2, Receipt } from "lucide-react";
import { prefillFromRecord } from "@/lib/invoice";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { contactFields, testKey, formatDate } from "@/lib/contact";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import RecordScans from "@/components/RecordScans";

const show = (v, type) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No") : type === "date" ? formatDate(v) : String(v));

function RelatedTable({ group, dbId }) {
  const cols = group.columns.filter((c) => c.name !== group.link).slice(0, 6);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`related-${testKey(group.table)}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-secondary">
        <p className="font-display font-bold flex items-center gap-2"><Table2 size={14} className="text-primary" /> {group.table}</p>
        <span className="font-mono text-xs text-muted-foreground" data-testid={`related-count-${testKey(group.table)}`}>
          {group.total} {group.total === 1 ? "record" : "records"} · by {group.link}
        </span>
      </div>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-xs">
          <thead><tr>{cols.map((c) => <th key={c.name} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{c.name}</th>)}</tr></thead>
          <tbody>
            {group.records.map((r) => (
              <tr key={r.id} className="border-t">{cols.map((c) => <td key={c.name} className="px-3 py-1.5 whitespace-nowrap max-w-[180px] truncate">{show(r.data[c.name], c.type)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {group.total > group.records.length && (
        <Link to={`/db/${dbId}/${encodeURIComponent(group.table)}`} className="block px-4 py-2 text-xs text-primary border-t hover:underline">
          Showing first {group.records.length} — open {group.table}
        </Link>
      )}
    </div>
  );
}

export default function RecordDetail({ record, columns, base, dbId, tableName, onClose, onEdit, onScansChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!record) return;
    setData(null);
    api.get(`${base}/records/${record.id}/related`).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  }, [record, base]);

  const f = contactFields(columns);
  const rec = data?.record || record;
  const v = (c) => rec?.data[c.name];
  const lines = (cols) => cols.map(v).filter((x) => x !== null && x !== undefined && x !== "");

  return (
    <Sheet open={!!record} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl bg-card overflow-y-auto" data-testid="record-detail">
        {rec && (
          <>
            <SheetHeader className="text-left">
              <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Contact card</p>
              <SheetTitle className="font-display text-3xl" data-testid="record-detail-title">{show(v(f.title))}</SheetTitle>
              {f.subtitle && v(f.subtitle) && <p className="text-muted-foreground">{v(f.subtitle)}</p>}
            </SheetHeader>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {[[MapPin, "Address", lines(f.address)], [Phone, "Phone", lines(f.phones)], [Mail, "Email", lines(f.emails)]].map(([Icon, label, vals]) => (
                <div key={label} className="border rounded-lg p-4" data-testid={`detail-${label.toLowerCase()}`}>
                  <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground"><Icon size={12} /> {label}</p>
                  <div className="mt-2 text-sm space-y-0.5">{vals.length ? vals.map((x, i) => <p key={i}>{x}</p>) : <p className="text-muted-foreground">—</p>}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="font-display text-xl font-bold">All fields</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={async () => {
                  const settings = (await api.get("/invoice-settings")).data;
                  navigate("/invoices/new", { state: { prefill: prefillFromRecord(rec, columns, dbId, tableName, settings) } });
                }} data-testid="detail-create-invoice-button" className="hover:bg-blue-600 hover:text-white"><Receipt size={14} /> Create invoice</Button>
                <Button size="sm" variant="outline" onClick={() => onEdit(rec)} data-testid="detail-edit-button"><Pencil size={14} /> Edit</Button>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm border rounded-lg p-4">
              {columns.filter((c) => c.type !== "binary").map((c) => (
                <div key={c.name} className="min-w-0">
                  <dt className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{c.name}</dt>
                  <dd className="truncate" title={show(v(c), c.type)}>{show(v(c), c.type)}</dd>
                </div>
              ))}
            </dl>
            {tableName !== "Customer Scans" && <RecordScans base={base} recordId={rec.id} onChanged={onScansChanged} />}
            <p className="mt-8 font-display text-xl font-bold">Linked records</p>
            <div className="mt-3 space-y-4" data-testid="related-section">
              {!data && <Loader2 className="animate-spin text-primary" />}
              {data && data.related.length === 0 && <p className="text-sm text-muted-foreground" data-testid="related-empty">No linked records in other tables.</p>}
              {data?.related.map((g) => <RelatedTable key={`${g.table}-${g.link}`} group={g} dbId={dbId} />)}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
